import 'focus-visible';
import create from '@verndale/core';
import modules from './modules';
import { addInnerContainer, addVideoModal, hideEmptySidebars } from './helpers';
import Expandables from './modules/expandables';
import ContentVideo from './modules/content-video';
import FormConsent from './modules/formConsent';
import { initializePrintableAssessment } from './helpers/printable-assessment';

const isStorybook = !!document.getElementById('storybook-root');
const body = document.querySelector('body') as HTMLBodyElement;

type JQueryLike = {
  fn?: Record<string, (...args: unknown[]) => unknown>;
};

// Make checkboxes clickable via their labels even without id/for attributes
const initCheckboxLabels = () => {
  const paragraphsWithCheckbox = document.querySelectorAll('p:has(input[type="checkbox"])');

  paragraphsWithCheckbox.forEach((p) => {
    const checkbox = p.querySelector('input[type="checkbox"]') as HTMLInputElement;
    const label = p.querySelector('label');

    if (checkbox && label && !label.hasAttribute('for')) {
      label.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        // If clicking on a link inside the label, let it navigate
        if (target.tagName === 'A' || target.closest('a')) {
          return;
        }

        e.preventDefault();
        checkbox.checked = !checkbox.checked;
        // Trigger change event for any listeners
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  });
};

const disableSubmitButtonConditionAnimations = () => {
  const windowWithJQuery = window as Window & {
    __hollisterSubmitAnimationPatchApplied?: boolean;
    jQuery?: JQueryLike;
  };
  const $ = windowWithJQuery.jQuery;

  if (
    windowWithJQuery.__hollisterSubmitAnimationPatchApplied ||
    !($ && $.fn && typeof $.fn.slideDown === 'function' && typeof $.fn.slideUp === 'function')
  ) {
    return;
  }

  windowWithJQuery.__hollisterSubmitAnimationPatchApplied = true;

  const isSubmitButtonContainer = (element: unknown) =>
    element instanceof HTMLElement &&
    element.matches('[data-sc-field-key]') &&
    !!element.querySelector('input.buttonSubmit[type="submit"]');

  const originalSlideDown = $.fn.slideDown;
  const originalSlideUp = $.fn.slideUp;
  const originalShow = typeof $.fn.show === 'function' ? $.fn.show : null;
  const originalHide = typeof $.fn.hide === 'function' ? $.fn.hide : null;

  $.fn.slideDown = function (this: ArrayLike<unknown>, ...args: unknown[]) {
    const elements = Array.from(this);

    if (elements.length > 0 && elements.every(isSubmitButtonContainer) && originalShow) {
      return originalShow.apply(this, args);
    }

    return originalSlideDown.apply(this, args);
  };

  $.fn.slideUp = function (this: ArrayLike<unknown>, ...args: unknown[]) {
    const elements = Array.from(this);

    if (elements.length > 0 && elements.every(isSubmitButtonContainer) && originalHide) {
      return originalHide.apply(this, args);
    }

    return originalSlideUp.apply(this, args);
  };
};

disableSubmitButtonConditionAnimations();

interface FxbConditionRule {
  fieldId: string;
  operator: string;
  value: string;
}

interface FxbConditionAction {
  fieldId: string;
  actionType: string;
  value: string | null;
}

interface FxbFieldCondition {
  matchType: string;
  conditions: FxbConditionRule[];
  actions: FxbConditionAction[];
}

interface FxbConditions {
  fieldConditions: FxbFieldCondition[];
}

/**
 * Enforces Sitecore FXB form conditions using CSS display instead of jQuery
 * animations. This is a fallback for mobile Safari where jQuery slideDown/slideUp
 * can fail silently, causing all form sections to remain visible.
 */
const enforceFxbConditions = () => {
  const forms = document.querySelectorAll<HTMLFormElement>('form[data-sc-fxb]');

  forms.forEach((form) => {
    const conditionInput = form.querySelector<HTMLInputElement>('[data-sc-fxb-condition]');
    if (!conditionInput) return;

    let parsed: FxbConditions;
    try {
      parsed = JSON.parse(conditionInput.value);
    } catch {
      return;
    }

    if (!parsed.fieldConditions?.length) return;

    const applyConditions = () => {
      parsed.fieldConditions.forEach((fc) => {
        const conditionsMet = fc.matchType === 'all'
          ? fc.conditions.every(evaluateCondition)
          : fc.conditions.some(evaluateCondition);

        fc.actions.forEach((action) => {
          const targetEl = form.querySelector<HTMLElement>(
            `[data-sc-field-key="${action.fieldId}"]`
          );
          if (!targetEl) return;

          if (action.actionType === 'hide') {
            targetEl.style.display = conditionsMet ? 'none' : '';
          } else if (action.actionType === 'show') {
            targetEl.style.display = conditionsMet ? '' : 'none';
          }
        });
      });
    };

    const evaluateCondition = (condition: FxbConditionRule): boolean => {
      const sourceEl = form.querySelector<HTMLElement>(
        `[data-sc-field-key="${condition.fieldId}"]`
      );
      if (!sourceEl) return false;

      const checkbox = sourceEl.tagName === 'INPUT'
        ? sourceEl as HTMLInputElement
        : sourceEl.querySelector<HTMLInputElement>('input[type="checkbox"]');
      const select = sourceEl.querySelector<HTMLSelectElement>('select');
      const radio = sourceEl.querySelector<HTMLInputElement>('input[type="radio"]:checked');

      let fieldValue = '';
      if (checkbox?.type === 'checkbox') {
        fieldValue = checkbox.checked ? 'true' : 'false';
      } else if (select) {
        fieldValue = select.value;
      } else if (radio) {
        fieldValue = radio.value;
      } else {
        const input = sourceEl.querySelector<HTMLInputElement>('input');
        fieldValue = input?.value ?? '';
      }

      switch (condition.operator) {
        case 'is equal to':
          return fieldValue === condition.value;
        case 'is not equal to':
          return fieldValue !== condition.value;
        case 'contains':
          return fieldValue.includes(condition.value);
        case 'does not contain':
          return !fieldValue.includes(condition.value);
        default:
          return false;
      }
    };

    // Apply immediately
    applyConditions();

    // Re-apply on any form input change
    form.addEventListener('change', applyConditions);
  });
};

document.addEventListener(
  'DOMContentLoaded',
  async () => {
    disableSubmitButtonConditionAnimations();

    if (!body.dataset.modulesLoaded || isStorybook) {
      create(modules).then(() => {
        body.dataset.modulesLoaded = 'true';
        // Initialize expandables after other modules
        Expandables.autoInit();
      });
    }
    hideEmptySidebars();
    addInnerContainer();
    addVideoModal();
    ContentVideo.autoInit();
    FormConsent.autoInit();

    // Initialize CMS assessment markup with the same behavior used in Storybook.
    document.querySelectorAll<HTMLElement>('.Printable-Form-Page').forEach(root => {
      initializePrintableAssessment(root);
    });

    // Initialize checkbox labels
    initCheckboxLabels();

    // Enforce FXB conditions as fallback for mobile Safari
    enforceFxbConditions();

    // Also try to initialize expandables here as backup
    setTimeout(() => {
      Expandables.autoInit();
      ContentVideo.autoInit();
      FormConsent.autoInit();
    }, 100);
  },
  false
);
